/**
 * RoleAwareOnboardingEntry — single /onboarding entry point.
 *
 * Dispatches to the correct onboarding by user.role so nothing overlaps:
 *   • team_member      → /welcome (employee orientation)
 *   • country_operator → Country Operator wizard
 *   • client_admin     → Subscriber Admin wizard (Sprint 2)
 *   • super_admin      → Subscriber Admin wizard (so they can demo / preview it)
 *   • anything else    → subscriber admin wizard as safe default
 *
 * NEW (Feb 2026): Subscribers who haven't seen the cinematic module intro yet
 * are routed to /welcome-intro first. The intro sets `konnect_intro_seen_v1`
 * in localStorage on completion, and then navigates back to /onboarding.
 * The intro is one-time and can be replayed manually.
 *
 * When a user CLICKS the sidebar "Setup Wizard" link, they want to see
 * a wizard — never redirect them away.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import OnboardingWizardPage from './OnboardingWizardPage';
import CountryOperatorOnboardingPage from './CountryOperatorOnboardingPage';

export default function RoleAwareOnboardingEntry() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading || !user) return;
    // Employees have their own dedicated /welcome orientation.
    if (user.role === 'team_member') {
      navigate('/welcome', { replace: true });
      return;
    }
    // Country operators skip the subscriber-focused intro — their wizard
    // already has its own agency-specific setup.
    if (user.role === 'country_operator') return;
    // Subscribers (client_admin) and super_admin see the cinematic module intro
    // once per browser. `konnect_intro_seen_v1` gates it.
    const seen = localStorage.getItem('konnect_intro_seen_v1');
    if (!seen) {
      navigate('/welcome-intro', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-gray-400 text-sm">Preparing your setup…</div>
      </div>
    );
  }

  if (user.role === 'country_operator') {
    return <CountryOperatorOnboardingPage />;
  }
  // client_admin, super_admin (for demo/preview), and anything else → subscriber wizard.
  return <OnboardingWizardPage />;
}
